// Status, priority, channel and SLA chips.
//
// The colours come from the `badge-*` / `prio-*` classes in web/src/styles/app.css
// rather than from utilities typed here, so a status looks identical in the queue, on
// the ticket, and in reports. The class names are authored CSS, not generated
// utilities, so composing one as `badge-${status}` is safe from Tailwind's scanner.

import {
  STATUS_LABELS, PRIORITY_LABELS, CHANNEL_LABELS, formatDateTime, visibleNamedStatus,
} from './format.js';

/**
 * `status` is always one of the six base statuses. `namedStatus` is the optional
 * layered status (docs/API.md §3.2) — it rolls up to the base one, so it is rendered
 * beside the badge rather than replacing it: the base status is what agents scan for.
 */
export function StatusBadge({ status, namedStatus }) {
  // Suppressed when it only repeats the badge — see `visibleNamedStatus`. "On hold"
  // beside "On-hold" is the same word twice and reads like a rendering fault.
  const named = visibleNamedStatus(status, namedStatus);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`badge badge-${status}`}>{STATUS_LABELS[status] ?? status}</span>
      {named && (
        <span className="text-xs text-ink-muted" title="Named status">
          {named}
        </span>
      )}
    </span>
  );
}

export function PriorityLabel({ priority }) {
  return (
    <span className={`text-sm prio-${priority}`}>{PRIORITY_LABELS[priority] ?? priority}</span>
  );
}

export function ChannelLabel({ channel }) {
  return <span className="text-sm text-ink-secondary">{CHANNEL_LABELS[channel] ?? channel}</span>;
}

/**
 * `sla_breached` is computed server-side (docs/API.md §3.1) — the first-reply
 * target was missed and no agent has replied yet. There is no resolution SLA in
 * the live configuration (migration 014), so this is the only clock the badge can
 * describe. Loud on purpose; it is the one row-level exception.
 */
export function SlaFlag({ breached, dueAt }) {
  if (!breached) return null;
  const due = formatDateTime(dueAt);
  return (
    <span
      className="badge badge-danger"
      title={due ? `First reply was due ${due}` : 'First reply overdue'}
    >
      SLA breached
    </span>
  );
}
